import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CourseCatalog from "./pages/CourseCatalog";
import CourseDetail from "./pages/CourseDetail";
import CoursePlayer from "./pages/CoursePlayer";
import InstructorDashboard from "./pages/InstructorDashboard";
import StudentDashboard from "./pages/StudentDashboard";
import CourseBuilder from "./pages/CourseBuilder";
import PricingPage from "./pages/PricingPage";
import InstructorPricingPage from "./pages/InstructorPricingPage";
import InstructorAnalytics from "./pages/InstructorAnalytics";
import PaymentSuccess from "./pages/PaymentSuccess";
import HelpCenter from "./pages/HelpCenter";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import RefundPolicy from "./pages/RefundPolicy";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import BlogManager from "./pages/BlogManager";
import AdminMessages from "./pages/AdminMessages";
import NewsletterComposer from "./pages/NewsletterComposer";
import KnowledgeBase from "./pages/KnowledgeBase";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCourses from "./pages/AdminCourses";
import AdminUsers from "./pages/AdminUsers";
import AdminCoupons from "./pages/AdminCoupons";
import AdminCreateCourse from "./pages/AdminCreateCourse";
import AdminEditCourse from "./pages/AdminEditCourse";
import AdminEnrollments from "./pages/AdminEnrollments";
import TrainingCourses from "./pages/TrainingCourses";
import TrainingCourseDetail from "./pages/TrainingCourseDetail";
import TrainingLesson from "./pages/TrainingLesson";
import PremiumCourses from "./pages/PremiumCourses";

// Academy Pages
import Academy from "./pages/Academy";
import AcademyCourses from "./pages/AcademyCourses";
import AcademyCourseDetail from "./pages/AcademyCourseDetail";
import AcademyLesson from "./pages/AcademyLesson";
import AcademyMembership from "./pages/AcademyMembership";
import AcademyDashboard from "./pages/AcademyDashboard";
import AcademyStartHere from "./pages/AcademyStartHere";
import AcademyFinalQuiz from "./pages/AcademyFinalQuiz";
import AcademyAdmin from "./pages/AcademyAdmin";
import AICoach from "./pages/AICoach";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AdminLeads from "./pages/AdminLeads";
import AdminUnconverted from "./pages/AdminUnconverted";
import NewsletterVerifyPage from "./pages/NewsletterVerifyPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Helper to get dashboard path based on role
const getDashboardPath = (role) => {
  switch (role) {
    case 'admin': return '/admin';
    case 'instructor': return '/instructor';
    default: return '/dashboard';
  }
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardPath(user.role)} /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={getDashboardPath(user.role)} /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to={getDashboardPath(user.role)} /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to={getDashboardPath(user.role)} /> : <ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/newsletter/verify" element={<NewsletterVerifyPage />} />
      <Route path="/courses" element={<CourseCatalog />} />
      <Route path="/premium" element={<PremiumCourses />} />
      <Route path="/courses/:id" element={<CourseDetail />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/instructor/pricing" element={<InstructorPricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/knowledge-base" element={<KnowledgeBase />} />
      <Route path="/training" element={<TrainingCourses />} />
      <Route path="/training/:slug" element={<TrainingCourseDetail />} />
      <Route path="/training/lesson/:slug" element={<TrainingLesson />} />
      
      {/* Student Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/learn/:courseId" element={
        <ProtectedRoute>
          <CoursePlayer />
        </ProtectedRoute>
      } />

      {/* Instructor Routes */}
      <Route path="/instructor" element={
        <ProtectedRoute allowedRoles={['instructor']}>
          <InstructorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/instructor/analytics" element={
        <ProtectedRoute allowedRoles={['instructor']}>
          <InstructorAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/instructor/courses/new" element={
        <ProtectedRoute allowedRoles={['instructor']}>
          <CourseBuilder />
        </ProtectedRoute>
      } />
      <Route path="/instructor/courses/:id/edit" element={
        <ProtectedRoute allowedRoles={['instructor']}>
          <CourseBuilder />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/blog" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <BlogManager />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminMessages />
        </ProtectedRoute>
      } />
      <Route path="/admin/newsletter" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <NewsletterComposer />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCourses />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/coupons" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCoupons />
        </ProtectedRoute>
      } />
      <Route path="/admin/enrollments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminEnrollments />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses/create" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCreateCourse />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses/:courseId/edit" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminEditCourse />
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLeads />
        </ProtectedRoute>
      } />
      <Route path="/admin/unconverted" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUnconverted />
        </ProtectedRoute>
      } />

      {/* Academy Routes */}
      <Route path="/academy" element={<Academy />} />
      <Route path="/academy/courses" element={<AcademyCourses />} />
      <Route path="/academy/course/:slug" element={<AcademyCourseDetail />} />
      <Route path="/academy/lesson/:lessonId" element={<AcademyLesson />} />
      <Route path="/academy/membership" element={<AcademyMembership />} />
      <Route path="/academy/membership/success" element={<AcademyMembership />} />
      <Route path="/academy/course/:slug/final-quiz" element={
        <ProtectedRoute>
          <AcademyFinalQuiz />
        </ProtectedRoute>
      } />
      <Route path="/academy/admin" element={
        <ProtectedRoute>
          <AcademyAdmin />
        </ProtectedRoute>
      } />
      <Route path="/academy/start-here" element={<AcademyStartHere />} />
      <Route path="/academy/coach" element={<AICoach />} />
      <Route path="/academy/dashboard" element={
        <ProtectedRoute>
          <AcademyDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
